import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, XCircle, AlertCircle, FileText, MapPin, Upload, RefreshCw } from 'lucide-react';
import { apiClient, endpoints } from '../api';
import './KYCProgress.css';
import KYCStatusBar from './KYCStatusBar';

const KYCProgress = ({ vendorData, onComplete, onEdit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [kycStatus, setKycStatus] = useState(vendorData?.status || 'DRAFT');
  const [reviewNotes, setReviewNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const getStepStatus = (stepId) => {
    switch (stepId) {
      case 1: // Registration
        return 'completed';
      case 2: // Location Setup
        return vendorData?.location ? 'completed' : 'pending';
      case 3: // KYC Documents
        if (kycStatus === 'DRAFT') return 'pending';
        if (kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW' || kycStatus === 'APPROVED' || kycStatus === 'REJECTED') {
          return 'completed';
        }
        return vendorData?.kyc ? 'completed' : 'pending';
      case 4: // Review
        if (kycStatus === 'SUBMITTED') return 'in-progress';
        if (kycStatus === 'UNDER_REVIEW') return 'in-progress';
        if (kycStatus === 'APPROVED') return 'completed';
        if (kycStatus === 'REJECTED') return 'rejected';
        return 'pending';
      default:
        return 'pending';
    }
  };

  const steps = [
    {
      id: 1,
      title: 'Registration',
      description: 'Basic vendor information',
      icon: FileText,
      status: getStepStatus(1),
      data: vendorData ? {
        shopName: vendorData.shopName,
        ownerName: vendorData.ownerName,
        email: vendorData.email
      } : null
    },
    {
      id: 2,
      title: 'Location Setup',
      description: 'Shop location and mapping',
      icon: MapPin,
      status: getStepStatus(2),
      data: vendorData?.location
    },
    {
      id: 3,
      title: 'KYC Documents',
      description: 'Document verification',
      icon: Upload,
      status: getStepStatus(3),
      data: vendorData?.kyc
    },
    {
      id: 4,
      title: 'Review',
      description: 'Under admin review',
      icon: Clock,
      status: getStepStatus(4)
    }
  ];

  useEffect(() => {
    // Fetch latest status on mount
    const fetchStatus = async () => {
      if (!vendorData?.id) return;
      try {
        const { data } = await apiClient.get(endpoints.kycOverallStatus(vendorData.id));
        if (data?.success && data?.data?.vendor) {
          const normalized = data.data.vendor.status === 'IN_REVIEW' ? 'UNDER_REVIEW' : data.data.vendor.status;
          setKycStatus(normalized);
          setReviewNotes(data.data.vendor.review_notes || '');
        }
      } catch (e) {}
    };
    fetchStatus();
  }, [vendorData?.id]);

  const getStepIcon = (step) => {
    const IconComponent = step.icon;
    const status = step.status;
    
    if (status === 'completed') {
      return <CheckCircle size={20} className="step-icon completed" />;
    } else if (status === 'in-progress') {
      return <Clock size={20} className="step-icon in-progress" />;
    } else if (status === 'rejected') {
      return <XCircle size={20} className="step-icon rejected" />;
    } else {
      return <IconComponent size={20} className="step-icon pending" />;
    }
  };

  const getStatusMessage = () => {
    switch (kycStatus) {
      case 'DRAFT':
        return {
          message: 'Complete your registration by uploading KYC documents',
          type: 'info'
        };
      case 'SUBMITTED':
        return {
          message: 'Submitted — Await verification. We will review your documents shortly.',
          type: 'info'
        };
      case 'UNDER_REVIEW':
        return {
          message: 'Under Review — Our team is verifying your documents. This may take 1-2 business days.',
          type: 'warning'
        };
      case 'APPROVED':
        return {
          message: 'Approved — Your vendor account is now active.',
          type: 'success'
        };
      case 'REJECTED':
        return {
          message: 'Rejected — Please review the feedback and resubmit your documents.',
          type: 'error'
        };
      default:
        return {
          message: 'Complete the registration process to get started',
          type: 'info'
        };
    }
  };

  const handleEditStep = (stepId) => {
    if (stepId === 1) {
      onEdit('registration');
    } else if (stepId === 2) {
      onEdit('location');
    } else if (stepId === 3) {
      onEdit('kyc');
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  const checkKYCStatus = async () => {
    if (!vendorData?.id) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.get(endpoints.kycOverallStatus(vendorData.id));
      if (response.status === 200 && response.data?.success && response.data?.data?.vendor) {
        setKycStatus(response.data.data.vendor.status);
      }
    } catch (error) {
      console.error('Error checking KYC status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshStatus = () => {
    checkKYCStatus();
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="kyc-progress-container">
      <div className="kyc-progress-card">
        <div className="kyc-progress-header">
          <div className="kyc-progress-logo">
            <FileText size={24} />
            <h2>KYC Progress</h2>
          </div>
          <p className="kyc-progress-subtitle">
            Track your vendor registration and verification status
          </p>
        </div>

        <div className="kyc-progress-content">
          <KYCStatusBar status={kycStatus} />
          <div className="status-message">
            <div className={`status-alert ${statusMessage.type}`}>
              {statusMessage.type === 'success' && <CheckCircle size={20} />}
              {statusMessage.type === 'warning' && <Clock size={20} />}
              {statusMessage.type === 'error' && <XCircle size={20} />}
              {statusMessage.type === 'info' && <AlertCircle size={20} />}
              <span>{statusMessage.message}</span>
              {(kycStatus === 'SUBMITTED' || kycStatus === 'UNDER_REVIEW') && (
                <button
                  type="button"
                  className="refresh-btn"
                  onClick={handleRefreshStatus}
                  disabled={isLoading}
                >
                  <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
                </button>
              )}
            </div>
          </div>

          <div className="steps-container">
            {steps.map((step, index) => (
              <div key={step.id} className="step-item">
                <div className="step-content">
                  <div className="step-header">
                    {getStepIcon(step)}
                    <div className="step-info">
                      <h4 className="step-title">{step.title}</h4>
                      <p className="step-description">{step.description}</p>
                    </div>
                    {step.status === 'completed' && step.data && (
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => handleEditStep(step.id)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  
                  {step.data && (
                    <div className="step-data">
                      {step.id === 1 && (
                        <div className="data-summary">
                          <p><strong>Shop:</strong> {step.data.shopName}</p>
                          <p><strong>Owner:</strong> {step.data.ownerName}</p>
                          <p><strong>Email:</strong> {step.data.email}</p>
                        </div>
                      )}
                      
                      {step.id === 2 && step.data && (
                        <div className="data-summary">
                          <p><strong>Address:</strong> {step.data.address}</p>
                          <p><strong>Coordinates:</strong> {step.data.coordinates?.lat?.toFixed(6)}, {step.data.coordinates?.lng?.toFixed(6)}</p>
                        </div>
                      )}
                      
              {step.id === 3 && step.data && (
                        <div className="data-summary">
                          <p><strong>Documents:</strong> {Object.keys(step.data.documents || {}).length} uploaded</p>
                          <p><strong>Submitted:</strong> {new Date(step.data.submittedAt).toLocaleDateString()}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {index < steps.length - 1 && (
                  <div className={`step-connector ${step.status === 'completed' ? 'completed' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {kycStatus === 'APPROVED' && (
            <div className="completion-section">
              <div className="completion-message">
                <CheckCircle size={48} className="success-icon" />
                <h3>Registration Complete!</h3>
                <p>Your vendor account is now active. You can start managing your shop and products.</p>
              </div>
              <button
                type="button"
                className="complete-btn"
                onClick={handleComplete}
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {kycStatus === 'REJECTED' && (
            <div className="rejection-section">
              <div className="rejection-message">
                <XCircle size={48} className="error-icon" />
                <h3>Documents Rejected</h3>
                <p>Please review the feedback and resubmit your documents.</p>
                {reviewNotes && (
                  <p style={{ color: '#991b1b', marginTop: 8 }}><strong>Reason:</strong> {reviewNotes}</p>
                )}
              </div>
              <div className="rejection-actions">
                <button
                  type="button"
                  className="resubmit-btn"
                  onClick={() => handleEditStep(3)}
                >
                  Resubmit Documents
                </button>
                <button
                  type="button"
                  className="contact-btn"
                >
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCProgress;
